import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import type { CSSProperties, MouseEvent } from 'react';
import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';

import { withErrorBoundary } from '../ErrorBoundary';
import { useWindowSize } from '../hooks/useWindowSize';
import Image from '../Image';
import VideoLock from '../VideoLock';

import { useVideoFromMas } from './hooks/useMediaAssetService';
import { useVideoEvents } from './hooks/useVideoEvents';
import { useVideoLock } from './hooks/useVideoLock';
import VideoJsonLd from './VideoJsonLd';

import type { EditorialContent } from '@cobalt/utilities';
import { getAdvertising, trackedVideoKinds } from '@services/JwPlayerService';
import JwPlayer from '@shared/JwPlayer';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { IS_MOBILE, IS_BAD_NETWORK_DETECTED } from '@utilities/browser';
import { Warn, Trace } from '@utilities/logging';
import { getUniqueId } from '@utilities/random';
import { competitionFolder, imgBaseUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

const videoProperties = {
  repeat: false,
  primary: 'html5',
} as const;

export type VideoPlayerProps = {
  teaser: EditorialContent;
  file?: string;
  fallbackImg?: string;
  videoId?: string;
  title?: string;
  description?: string;
  isAutoPlay?: boolean | 'viewable';
  isMuted?: boolean;
  controls?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  disableFullscreen?: boolean;
  repeat?: boolean;
  enablePrerolls?: boolean;
  minimizeOnScroll?: boolean;
  minimizeOnScrollMobile?: boolean;
  checkInteraction?: boolean;
  watchOtherVideos?: boolean;
  unmuteOnFullscreen?: boolean;
  fullscreenOnClick?: boolean;
  aspectratio?: string;
  stretching?: 'uniform' | 'exactfit' | 'fill' | 'none';
  preload?: 'none' | 'metadata' | 'auto';
  context?: string;
  sponsor?: string;
  maxTimeAgo?: number;
  blockHighlights?: boolean;
  activateLogin?: boolean;
  sharing?: boolean;
  sharingUrl?: string;
  sharingTxt?: string;
  className?: string;
  wrapperClassName?: string;
  logo?: jwplayer.LogoConfig;
  forceSquareOnMobile?: boolean;
  onJwPlayerInit?: (jwplayer: jwplayer.JWPlayer) => unknown;
  disableAutoPause?: boolean;
};

const defaultFallbackImg = competitionFolder
  ? `${imgBaseUrl}/imgml/uefacom/${competitionFolder}/fallback.jpg`
  : `${imgBaseUrl}/imgml/void.png`;

const VideoPlayer = ({
  teaser,
  file,
  fallbackImg = defaultFallbackImg,
  videoId = teaser?.id,
  title = teaser?.title,
  description = teaser?.description,
  isAutoPlay = 'viewable',
  isMuted = !!isAutoPlay,
  controls = true,
  showTitle = true,
  showDescription,
  disableFullscreen,
  repeat = false,
  enablePrerolls = true,
  minimizeOnScroll = false,
  minimizeOnScrollMobile = false,
  checkInteraction = true,
  watchOtherVideos = true,
  unmuteOnFullscreen,
  fullscreenOnClick,
  aspectratio = '16:9',
  stretching,
  preload = 'metadata',
  context,
  sponsor = teaser?.sponsor?.name ?? teaser?.sponsors?.at(0),
  logo,
  maxTimeAgo,
  blockHighlights,
  activateLogin,
  sharing,
  sharingUrl,
  sharingTxt,
  className,
  wrapperClassName,
  forceSquareOnMobile,
  onJwPlayerInit,
  disableAutoPause,
}: VideoPlayerProps) => {
  const playerId = useMemo(() => getUniqueId(videoId), [videoId]);
  const player = useRef<jwplayer.JWPlayer>(null);

  const playing = useRef<boolean>(false);
  const isFullscreen = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement & HTMLButtonElement>(null);
  const interacted = useRef(false);

  const [showThumbnail, setShowThumbnail] = useState(true);

  const onMinimizeClose = () => {
    player.current.pause();
    interacted.current = false;
    setMinimized(false);
  };

  const closeButton = useRef<[icon: string, label: string, handler: () => void, id: string, className?: string]>([
    '',
    'Close',
    onMinimizeClose,
    'closeMinimize',
  ]);

  const [hasError, setHasError] = useState(false);

  const masResult = useVideoFromMas(file ? null : teaser, true);

  const [stream, setStream] = useState<string>(file ?? masResult?.hlsStreamUrl);
  useEffect(() => {
    if (stream && !hasError) return;

    setStream(file ?? masResult?.hlsStreamUrl);
  }, [file, stream, masResult, hasError]);

  const [minimized, setMinimized] = useState(false);

  const inview = useViewportSpy(containerRef, { threshold: [0.1, 0.5, 1] });
  const { isMobile } = useWindowSize();

  const videoLocked = useVideoLock({
    activateLogin,
    blockHighlights,
    maxTimeAgo,
    publicationDate: teaser?.publicationDate,
    videoKind: teaser?.classification?.kind,
  });

  const { sendEvent } = useVideoEvents((data: { type: string; videoId: string }) => {
    if (!data || data.videoId === videoId) return;

    if (!player.current || !watchOtherVideos) return;

    if (data.type === 'playerMinimized' || data.type === 'userInteracted') {
      player.current.pause();

      if (data.type === 'userInteracted') interacted.current = false;

      setMinimized(false);
    }
  }, videoId);

  const handleUserInteraction = useCallback(() => {
    if (interacted.current) return;

    Trace(`user interacted with video ${videoId}`);
    interacted.current = true;
    sendEvent({ type: 'userInteracted' });
  }, [sendEvent, videoId]);

  useEffect(() => {
    const mini =
      ((IS_MOBILE && minimizeOnScrollMobile) || minimizeOnScroll) &&
      (!checkInteraction || interacted.current) &&
      !inview;
    setMinimized(mini);
    if (mini) {
      // minimize player
      player.current?.addButton(...closeButton.current);
      sendEvent({ type: 'playerMinimized' });
    } else {
      // return to inpage player
      player.current?.removeButton('closeMinimize');
    }
  }, [inview, minimizeOnScroll, sendEvent, minimizeOnScrollMobile, checkInteraction]);

  const advertising = useMemo(
    () =>
      enablePrerolls &&
      getAdvertising(
        teaser?.classification?.kind?.toUpperCase(),
        teaser?.classification?.section,
        teaser?.classification?.kind,
        context,
        sponsor,
        null,
        teaser?.classification?.contentScoring,
      ),
    [
      enablePrerolls,
      teaser?.classification?.kind,
      teaser?.classification?.section,
      context,
      sponsor,
      teaser?.classification?.contentScoring,
    ],
  );

  const thumbnail = useMemo(() => {
    const size = IS_BAD_NETWORK_DETECTED ? '4' : '1';

    const imageSizes = {
      '1:1': teaser?.mainMedia?.image?.square?.[`s${size}`] || teaser?.imageUrl?.replace('w1.jpg', `s${size}.jpg`),
      '9:16': teaser?.mainMedia?.image?.portrait?.[`p${size}`] || teaser?.imageUrl?.replace('w1.jpg', `p${size}.jpg`),
      '16:9': teaser?.mainMedia?.image?.wide?.[`w${size}`] || teaser?.imageUrl,
    } as const;

    return imageSizes[aspectratio] || imageSizes['16:9'] || fallbackImg;
  }, [aspectratio, fallbackImg, teaser]);

  const videoType = useMemo(() => {
    const kind = teaser?.classification?.kind?.toLowerCase();
    return trackedVideoKinds.includes(kind) ? kind : 'video';
  }, [teaser?.classification?.kind]);

  const sharingOptions = useMemo<jwplayer.SharingConfig>(() => {
    if (!sharing || !sharingUrl) return null;

    return {
      link: sharingUrl.startsWith('//') ? location.protocol + sharingUrl : sharingUrl,
      heading: sharingTxt,
      sites: ['facebook', 'twitter'],
    };
  }, [sharing, sharingTxt, sharingUrl]);

  const onPlayerReady = (p: jwplayer.JWPlayer) => {
    if (!p) return;
    player.current = p;

    // set external player reference
    onJwPlayerInit?.(p);

    p.on('ready', () => {
      setHasError(false);
      p.getContainer().addEventListener('click', handleUserInteraction);
    });

    p.on('firstFrame', (e: jwplayer.FirstFrameParam) => {
      setShowThumbnail(false);
      sendEvent(e);
    });

    p.on('play', (e: jwplayer.PlayParam) => {
      playing.current = true;
      sendEvent(e);
    });

    p.on('pause', (e: jwplayer.PauseParam) => {
      playing.current = false;
      sendEvent(e);
    });

    p.on('error', (e: jwplayer.ErrorParam) => {
      playing.current = false;
      setHasError(true);
      setShowThumbnail(true);
      Warn(e);
    });

    p.on('complete', (e: jwplayer.CompleteParam) => {
      playing.current = false;
      sendEvent(e);
    });

    p.on('fullscreen', (e: jwplayer.FullscreenParam) => {
      sendEvent(e);
      isFullscreen.current = e.fullscreen;

      unmuteOnFullscreen && p.setMute(!e.fullscreen);

      if (!fullscreenOnClick) return;

      if (e.fullscreen) {
        p.play();
        setTimeout(() => {
          p.play();
        }, 1000);

        GoogleTagManagerHelper.event(
          'Videos',
          'Fullscreen',
          title,
          { videoId, videoType, videoTitle: title, videoLength: Math.round(p.getDuration()) },
          null,
          false,
          'Video Event',
        );
      }
    });
  };

  const handleContainerClick = (e: MouseEvent<HTMLElement>) => {
    if (isFullscreen.current || !fullscreenOnClick) return;

    e.stopPropagation();
    player.current?.setFullscreen(true);
    return false;
  };

  const isMobileSquare = forceSquareOnMobile && isMobile;
  const responsiveAspectRatio = isMobileSquare ? '1:1' : aspectratio;
  const responsiveStretching = isMobileSquare ? 'fill' : stretching;
  const { sponsor: sponsorObj, loaded } = useSponsor(sponsor);

  const customProps: jwplayer.SetupConfig = useMemo(
    () => ({
      ...videoProperties,
      skin: {
        name: teaser.skin ?? 'uefacom',
        active: teaser.activecolor ?? null,
      },
      videoId,
      displaytitle: showTitle,
      displaydescription: showDescription,
      autostart: IS_BAD_NETWORK_DETECTED ? false : isAutoPlay,
      autoPause: isAutoPlay &&
        !disableAutoPause && {
          viewability: true,
          pauseAds: true,
        },
      doNotSaveCookies: true,
      mute: isMuted,
      sharing: sharingOptions,
      allowFullscreen: !disableFullscreen,
      controls,
      repeat,
      aspectratio: responsiveAspectRatio,
      stretching: responsiveStretching,
      preload,
      videoType,
      advertising,
      logo,
      videoSponsor: sponsorObj?.code,
    }),
    [
      videoId,
      showTitle,
      showDescription,
      isAutoPlay,
      isMuted,
      sharingOptions,
      disableFullscreen,
      controls,
      repeat,
      responsiveAspectRatio,
      responsiveStretching,
      preload,
      videoType,
      advertising,
      logo,
      sponsorObj?.code,
      teaser.skin,
      teaser.activecolor,
      disableAutoPause,
    ],
  );

  const playlist = useMemo<jwplayer.PlaylistItem[]>(
    () => [
      {
        file: stream,
        mediaid: videoId,
        videoId,
        title,
        description,
        image: thumbnail,
      },
    ],
    [description, stream, thumbnail, title, videoId],
  );

  const playerConfig = useMemo<jwplayer.SetupConfig>(
    () => ({
      ...customProps,
      playlist,
    }),
    [customProps, playlist],
  );

  const btnAttrs = fullscreenOnClick ? { onClick: handleContainerClick, role: 'button' } : {}; // esc doesn't work in firefox inside of <button> tag
  return (
    <div
      className={clsx({
        [classes.container]: true,
        [wrapperClassName]: !!wrapperClassName,
        [classes.minimized]: minimized,
      })}
      style={
        {
          '--jwplayer--aspect-ratio': aspectratio?.replace(':', '/'),
        } as CSSProperties
      }
      aria-label={title}
      ref={containerRef}
      {...btnAttrs}
    >
      {teaser && <VideoJsonLd video={teaser} />}
      {!!thumbnail && showThumbnail && (
        <Image className={clsx(classes.poster)} title={title} src={thumbnail} fallback={fallbackImg} />
      )}
      {videoLocked && <VideoLock videoid={videoId} videoTitle={title} videoKind={teaser?.classification?.kind} />}
      {!videoLocked && !!stream && loaded && (
        <JwPlayer
          id={playerId}
          configuration={playerConfig}
          ref={onPlayerReady}
          className={clsx(classes.wrapper, className)}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(memo(VideoPlayer));
